// src/context/StudentFormContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { studentFormService } from '../services/studentFormService';
import { getCurrentUser } from '../services/authService';
import { useMemo } from "react";

const StudentFormContext = createContext();

export const useStudentFormContext = () => {
    const context = useContext(StudentFormContext);
    if (!context) {
        throw new Error('useStudentFormContext must be used within StudentFormProvider');
    }
    return context;
};

export const StudentFormProvider = ({ children }) => {
    const [forms, setForms] = useState([]);
    const [myForm, setMyForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [initialized, setInitialized] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);

    // Load current user on mount
    useEffect(() => {
        const user = getCurrentUser();
        console.log('Current user loaded in context:', user);
        setCurrentUser(user);
    }, []);

    // Load all forms (HR/Admin only)
    const loadAllForms = useCallback(async () => {
        const isAdminOrHR = currentUser?.role === 'admin' || currentUser?.role === 'hr_manager';

        if (!isAdminOrHR) {
            setForms([]);
            return;
        }

        try {
            console.log('Loading all forms...');
            const response = await studentFormService.getAllForms();
            console.log('Forms loaded:', response);

            let formData = [];
            if (response && response.results) {
                formData = response.results;
            } else if (Array.isArray(response)) {
                formData = response;
            }

            setForms(formData);
            setError(null);
        } catch (err) {
            console.error('Error loading forms:', err);
            setError(err.message || 'Failed to load forms');
            setForms([]);
        }
    }, [currentUser]);

    // Load employee's own form (Employee only)
    const loadMyForm = useCallback(async () => {
        if (currentUser?.role !== 'employee') {
            setMyForm(null);
            return;
        }

        if (!currentUser?.employee_id) {
            setMyForm(null);
            return;
        }

        try {
            console.log('Loading my form...');
            const response = await studentFormService.getMyForm(currentUser.employee_id);
            console.log('My form loaded:', response);

            let formData = null;
            if (response) {
                if (Array.isArray(response) && response.length > 0) {
                    formData = response[0];
                } else if (!Array.isArray(response)) {
                    formData = response;
                }
            }

            setMyForm(formData || null);
            setError(null);
        } catch (err) {
            if (err.status === 404 || err.response?.status === 404) {
                setMyForm(null);
                setError(null);
            } else {
                console.error('Error loading my form:', err);
                setError(err.message || 'Failed to load your form');
                setMyForm(null);
            }
        }
    }, [currentUser]);

    // Create new form - FIXED: get user from localStorage directly
    const createForm = async (formData) => {
        // Get user directly from localStorage to ensure we have the latest
        const user = getCurrentUser();
        console.log('Creating form - Current user from localStorage:', user);

        if (!user || !user.id) {
            console.error('No user found in localStorage');
            throw new Error('Please login again to submit the form.');
        }

        setLoading(true);
        setError(null);
        try {
            const data = {
                employee: user.id,
                ...formData
            };
            console.log('Submitting form data:', data);
            const response = await studentFormService.createForm(data);
            console.log('Form created:', response);
            await loadMyForm();
            await loadAllForms();
            return response;
        } catch (err) {
            console.error('Error creating form:', err);
            const errorMsg = err.response?.data?.error ||
                err.response?.data?.enrolment_number?.[0] ||
                err.message || 'Failed to create form';
            setError(errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Update own form
    const updateMyForm = async (formData) => {
        // Get user directly from localStorage
        const user = getCurrentUser();
        console.log('Updating form - Current user from localStorage:', user);

        if (!user || !user.id) {
            console.error('No user found in localStorage');
            throw new Error('Please login again to update the form.');
        }

        // Check if we have a form to update
        if (!myForm || !myForm.id) {
            console.error('No form to update or form has no ID:', myForm);
            // Try to load the form first
            await loadMyForm();
            if (!myForm || !myForm.id) {
                throw new Error('No existing form to update. Please submit a new form instead.');
            }
        }

        setLoading(true);
        setError(null);
        try {
            console.log('Updating form with ID:', myForm.id);
            const response = await studentFormService.updateForm(myForm.id, formData);
            console.log('Form updated:', response);
            // Update myForm with the updated data
            setMyForm(response);
            await loadAllForms();
            return response;
        } catch (err) {
            console.error('Error updating form:', err);
            const errorMsg = err.response?.data?.error ||
                err.response?.data?.enrolment_number?.[0] ||
                err.message || 'Failed to update form';
            setError(errorMsg);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // HR Update
    const hrUpdateForm = async (id, data) => {
        const isAdminOrHR = currentUser?.role === 'admin' || currentUser?.role === 'hr_manager';

        if (!isAdminOrHR) {
            throw new Error('Only HR or Admin can perform this action');
        }

        setLoading(true);
        setError(null);
        try {
            const response = await studentFormService.hrUpdate(id, data);
            console.log('HR update:', response);
            await loadAllForms();
            return response;
        } catch (err) {
            console.error('Error HR updating form:', err);
            setError(err.response?.data?.error || err.message || 'Failed to update form');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Delete form
    const deleteForm = async (id) => {
        const isAdminOrHR = currentUser?.role === 'admin' || currentUser?.role === 'hr_manager';

        if (!isAdminOrHR) {
            throw new Error('Only HR or Admin can perform this action');
        }

        setLoading(true);
        setError(null);
        try {
            await studentFormService.deleteForm(id);
            console.log('Form deleted');
            await loadAllForms();
            if (myForm && myForm.id === id) {
                setMyForm(null);
            }
        } catch (err) {
            console.error('Error deleting form:', err);
            setError(err.message || 'Failed to delete form');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Refresh data
    const refreshData = useCallback(() => {
        setRefreshTrigger(prev => prev + 1);
    }, []);

    // Load data on mount and refresh based on role
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);

            try {
                if (currentUser?.role === "admin" || currentUser?.role === "hr_manager") {
                    await loadAllForms();
                } else if (currentUser?.role === "employee") {
                    await loadMyForm();
                }
            } finally {
                setLoading(false);
                setInitialized(true);
            }
        };

        if (currentUser) {
            loadData();
        } else {
            // If no current user, check localStorage again
            const user = getCurrentUser();
            if (user) {
                setCurrentUser(user);
                loadData();
            } else {
                setLoading(false);
                setInitialized(true);
            }
        }
    }, [refreshTrigger, currentUser, loadAllForms, loadMyForm]);

    const value = {
        forms,
        myForm,
        loading,
        error,
        initialized,
        loadAllForms,
        loadMyForm,
        createForm,
        updateMyForm,
        hrUpdateForm,
        deleteForm,
        refreshData,
        hasForm: !!myForm,
        isAdminOrHR: currentUser?.role === 'admin' || currentUser?.role === 'hr_manager',
        currentUser
    };

    return (
        <StudentFormContext.Provider value={value}>
            {children}
        </StudentFormContext.Provider>
    );
};